// Mirror of backend `models/src/bi/viz.ts`.
import type { BiFormatSpec } from './format';

export enum BiVizType {
  Line = 'line',
  Area = 'area',
  Bar = 'bar',
  HorizontalBar = 'horizontal_bar',
  StackedBar = 'stacked_bar',
  Pie = 'pie',
  Donut = 'donut',
  /** Spokes: the values of `x`; one polygon per series. */
  Radar = 'radar',
  /** Two-sided horizontal bars per category, one side per series. */
  Pyramid = 'pyramid',
  /** Ordered stages narrowing down: measures in order, or one dimension. */
  Funnel = 'funnel',
  Table = 'table',
  Kpi = 'kpi',
  /** Several headline numbers side by side, from a one-row result. */
  KpiGroup = 'kpi_group',
}

export type BiChartType = Exclude<
  BiVizType,
  BiVizType.Table | BiVizType.Kpi | BiVizType.KpiGroup | BiVizType.Funnel
>;

/** A display format's shorthand: its kind (spec §6.6). */
export enum BiFormat {
  Integer = 'integer',
  Decimal = 'decimal',
  Currency = 'currency',
  Percent = 'percent',
  /** Minutes. */
  Duration = 'duration',
}

/** How one series of a combo chart is drawn. */
export enum BiSeriesType {
  Bar = 'bar',
  Line = 'line',
  Area = 'area',
}

export enum BiAxisSide {
  Left = 'left',
  /** A second value axis, for a measure on another scale. */
  Right = 'right',
}

export interface BiVizSeries {
  /** A measure key of the result. */
  key: string;
  name?: string;
  /**
   * Combo charts only ({@link kBiComboChartTypes}). Omitted: the chart's
   * own type (`bar` on a stacked bar). Only `bar` series stack.
   */
  as?: BiSeriesType;
  /** Combo charts only. Omitted: left. */
  axis?: BiAxisSide;
}

export interface BiChartViz {
  type: BiChartType;
  /** The dimension key on the category / time axis. */
  x: string;
  series: BiVizSeries[];
  /**
   * The second dimension key, whose values become the series, when the
   * query has two dimensions. Exactly one measure is charted then.
   */
  seriesBy?: string;
  /** Per measure key: overrides the column's own format. */
  formats?: Record<string, BiFormatSpec>;
  style?: BiVizStyle;
}

/** A funnel's stages as the values of one dimension, in their order. */
export interface BiFunnelStagesBy {
  /** The dimension key. */
  key: string;
  /** Its values as text, first stage first; other values are not drawn. */
  order: string[];
}

/**
 * Ordered stages, each with its value, the rate from the stage before and
 * the overall rate from the first (spec §6.4.3).
 */
export interface BiFunnelViz {
  type: BiVizType.Funnel;
  /**
   * Without `stagesBy`: the stages, one measure each in order, read from a
   * one-row result. With it: the one measure every stage takes.
   */
  series: { key: string; name?: string }[];
  stagesBy?: BiFunnelStagesBy;
  /** Per measure key: overrides the column's own format. */
  formats?: Record<string, BiFormatSpec>;
  style?: BiVizStyle;
}

export interface BiTableViz {
  type: BiVizType.Table;
  columns: { key: string; name?: string }[];
  /** Per measure key: overrides the column's own format. */
  formats?: Record<string, BiFormatSpec>;
  style?: BiVizStyle;
}

export enum BiKpiCompare {
  PreviousPeriod = 'previous_period',
}

export interface BiKpiViz {
  type: BiVizType.Kpi;
  /** A measure key of the result. */
  value: string;
  /** Needs a time range on the query. */
  compare?: BiKpiCompare;
  /** Overrides the value column's own format. */
  format?: BiFormatSpec;
  style?: BiVizStyle;
}

/** The fewest and most numbers a KPI group shows. */
export const kBiMinKpiGroupItems = 2;
export const kBiMaxKpiGroupItems = 6;

export interface BiKpiGroupItem {
  /** A measure key of the result. */
  value: string;
  /** Omitted: the column's label. */
  name?: string;
  /** The label's color; omitted: the palette's, in item order. */
  color?: BiColor;
  /** Overrides the value column's own format. */
  format?: BiFormatSpec;
}

export interface BiKpiGroupViz {
  type: BiVizType.KpiGroup;
  /** 2–6 items, in display order. */
  items: BiKpiGroupItem[];
  /** Every item against its previous period; needs a time range. */
  compare?: BiKpiCompare;
  style?: BiVizStyle;
}

export type BiVizSpec =
  | BiChartViz
  | BiFunnelViz
  | BiTableViz
  | BiKpiViz
  | BiKpiGroupViz;

/** A single KPI or a KPI group: headline numbers over one row. */
export function isBiKpiViz(viz: BiVizSpec): viz is BiKpiViz | BiKpiGroupViz {
  return viz.type === BiVizType.Kpi || viz.type === BiVizType.KpiGroup;
}

/** Whether the viz shows a change against the previous period. */
export function biVizComparesPrevious(viz: BiVizSpec): boolean {
  return isBiKpiViz(viz) && viz.compare === BiKpiCompare.PreviousPeriod;
}

/** A chart with a category `x` and series: not a funnel, table or KPI. */
export function isBiChartViz(viz: BiVizSpec): viz is BiChartViz {
  return (
    viz.type !== BiVizType.Table &&
    !isBiKpiViz(viz) &&
    viz.type !== BiVizType.Funnel
  );
}

/**
 * A color from the client's theme. Colors are only ever tokens, never hex,
 * so light / dark and brand changes stay consistent; the client owns the
 * value each token renders as.
 */
export enum BiColor {
  Blue = 'blue',
  Sky = 'sky',
  Cyan = 'cyan',
  Teal = 'teal',
  Green = 'green',
  Lime = 'lime',
  Yellow = 'yellow',
  Amber = 'amber',
  Orange = 'orange',
  Red = 'red',
  Rose = 'rose',
  Pink = 'pink',
  Purple = 'purple',
  Violet = 'violet',
  Indigo = 'indigo',
  Navy = 'navy',
  Slate = 'slate',
  Gray = 'gray',
  Gold = 'gold',
  Brown = 'brown',
  Olive = 'olive',
  Pine = 'pine',
  Emerald = 'emerald',
  Mint = 'mint',
  Lavender = 'lavender',
  Coral = 'coral',
  /** The muted tones the Morandi palette is made of. */
  Sage = 'sage',
  Mauve = 'mauve',
  Mist = 'mist',
  Sand = 'sand',
  Taupe = 'taupe',
  Wine = 'wine',
}

/** A predefined set of series colors, chosen per dashboard or per card. */
export enum BiPalette {
  Default = 'default',
  Warm = 'warm',
  Cool = 'cool',
  Calm = 'calm',
  Vivid = 'vivid',
  Mono = 'mono',
  Business = 'business',
  Morandi = 'morandi',
  Earth = 'earth',
  Forest = 'forest',
  Candy = 'candy',
  Ocean = 'ocean',
  Sunset = 'sunset',
  Retro = 'retro',
  Chinese = 'chinese',
}

/**
 * Each palette's colors, in the order series take them (the n-th series
 * takes color n, wrapping around). The one source the client mirrors.
 */
export const kBiPaletteColors: Record<BiPalette, readonly BiColor[]> = {
  [BiPalette.Default]: [
    BiColor.Blue,
    BiColor.Green,
    BiColor.Amber,
    BiColor.Red,
    BiColor.Violet,
    BiColor.Pink,
    BiColor.Cyan,
    BiColor.Lime,
  ],
  [BiPalette.Warm]: [
    BiColor.Red,
    BiColor.Orange,
    BiColor.Amber,
    BiColor.Rose,
    BiColor.Yellow,
    BiColor.Pink,
    BiColor.Purple,
    BiColor.Lime,
  ],
  [BiPalette.Cool]: [
    BiColor.Blue,
    BiColor.Teal,
    BiColor.Indigo,
    BiColor.Cyan,
    BiColor.Violet,
    BiColor.Sky,
    BiColor.Green,
    BiColor.Navy,
  ],
  [BiPalette.Calm]: [
    BiColor.Teal,
    BiColor.Sky,
    BiColor.Slate,
    BiColor.Indigo,
    BiColor.Green,
    BiColor.Violet,
    BiColor.Gray,
    BiColor.Cyan,
  ],
  [BiPalette.Vivid]: [
    BiColor.Pink,
    BiColor.Cyan,
    BiColor.Orange,
    BiColor.Violet,
    BiColor.Lime,
    BiColor.Red,
    BiColor.Blue,
    BiColor.Yellow,
  ],
  [BiPalette.Mono]: [
    BiColor.Navy,
    BiColor.Blue,
    BiColor.Sky,
    BiColor.Indigo,
    BiColor.Slate,
    BiColor.Gray,
  ],
  [BiPalette.Business]: [
    BiColor.Navy,
    BiColor.Blue,
    BiColor.Slate,
    BiColor.Gold,
    BiColor.Teal,
    BiColor.Sky,
    BiColor.Gray,
    BiColor.Indigo,
  ],
  [BiPalette.Morandi]: [
    BiColor.Mist,
    BiColor.Mauve,
    BiColor.Sage,
    BiColor.Sand,
    BiColor.Taupe,
    BiColor.Slate,
    BiColor.Gray,
  ],
  [BiPalette.Earth]: [
    BiColor.Brown,
    BiColor.Amber,
    BiColor.Olive,
    BiColor.Sand,
    BiColor.Pine,
    BiColor.Orange,
    BiColor.Taupe,
    BiColor.Gold,
  ],
  [BiPalette.Forest]: [
    BiColor.Pine,
    BiColor.Emerald,
    BiColor.Lime,
    BiColor.Olive,
    BiColor.Teal,
    BiColor.Sage,
    BiColor.Mint,
    BiColor.Green,
  ],
  [BiPalette.Candy]: [
    BiColor.Pink,
    BiColor.Sky,
    BiColor.Mint,
    BiColor.Yellow,
    BiColor.Lavender,
    BiColor.Coral,
    BiColor.Cyan,
    BiColor.Lime,
  ],
  [BiPalette.Ocean]: [
    BiColor.Navy,
    BiColor.Sky,
    BiColor.Teal,
    BiColor.Blue,
    BiColor.Mint,
    BiColor.Cyan,
    BiColor.Mist,
    BiColor.Indigo,
  ],
  [BiPalette.Sunset]: [
    BiColor.Purple,
    BiColor.Rose,
    BiColor.Orange,
    BiColor.Gold,
    BiColor.Coral,
    BiColor.Mauve,
    BiColor.Amber,
    BiColor.Violet,
  ],
  [BiPalette.Retro]: [
    BiColor.Teal,
    BiColor.Gold,
    BiColor.Coral,
    BiColor.Navy,
    BiColor.Olive,
    BiColor.Sand,
    BiColor.Brown,
    BiColor.Wine,
  ],
  [BiPalette.Chinese]: [
    BiColor.Red,
    BiColor.Gold,
    BiColor.Wine,
    BiColor.Emerald,
    BiColor.Navy,
    BiColor.Amber,
    BiColor.Coral,
    BiColor.Sand,
  ],
};

/** What new dashboards, and dashboards from before palettes, use. */
export const kBiDefaultPalette = BiPalette.Default;

export enum BiTitleAlign {
  Left = 'left',
  Center = 'center',
}

export enum BiLegendPosition {
  Top = 'top',
  Bottom = 'bottom',
  Right = 'right',
}

export type BiLabelRotate = 0 | 45 | 90;
export type BiLineWidth = 1 | 2 | 3;
export type BiCornerRadius = 0 | 4 | 8;

export enum BiBarGap {
  Narrow = 'narrow',
  Normal = 'normal',
  Wide = 'wide',
}

export enum BiTableDensity {
  Compact = 'compact',
  Normal = 'normal',
}

/** What a funnel's stage labels show beside each stage's name. */
export enum BiFunnelShow {
  Values = 'values',
  Rates = 'rates',
  Both = 'both',
}

export enum BiDeltaDirection {
  Up = 'up',
  Down = 'down',
}

export interface BiCategoryAxisStyle {
  show: boolean;
  label?: string;
  labelRotate?: BiLabelRotate;
}

export interface BiValueAxisStyle {
  show: boolean;
  label?: string;
  min?: number;
  max?: number;
}

/**
 * How a chart, table or KPI looks, apart from what it shows. Every field is
 * optional and falls back to the theme; each applies only to the viz types
 * {@link biVizStyleOptionsFor} lists, and the parser refuses the rest.
 */
export interface BiVizStyle {
  title?: { show: boolean; align?: BiTitleAlign };
  subtitle?: string;
  /** Overrides the dashboard's palette for this card. */
  palette?: BiPalette;
  /** Per measure key, or per `seriesBy` / pie slice value. */
  seriesColors?: Record<string, BiColor>;
  legend?: { show: boolean; position: BiLegendPosition };
  axes?: {
    x?: BiCategoryAxisStyle;
    y?: BiValueAxisStyle;
    /** The right axis: only with a series on it. */
    y2?: BiValueAxisStyle;
  };
  gridLines?: boolean;
  /** Values on bars / points / slices. */
  dataLabels?: boolean;
  line?: { width: BiLineWidth; smooth: boolean; showPoints: boolean };
  bar?: { cornerRadius: BiCornerRadius; gap: BiBarGap };
  /** `innerRadius` 0..0.8 of the radius. */
  pie?: { innerRadius: number; showPercent: boolean };
  /** `fillOpacity` 0..1 of each polygon's fill. */
  radar?: { fillOpacity: number };
  funnel?: { show: BiFunnelShow };
  /** Also the chart / table toggle's table on a chart. */
  table?: { density: BiTableDensity; zebra: boolean; totalsRow: boolean };
  /** Needs `compare` on the KPI. */
  kpi?: { showDelta: boolean; deltaGoodWhen: BiDeltaDirection };
}

export type BiVizStyleOption = keyof BiVizStyle;

export const kBiVizStyleOptions: readonly BiVizStyleOption[] = [
  'title',
  'subtitle',
  'palette',
  'seriesColors',
  'legend',
  'axes',
  'gridLines',
  'dataLabels',
  'line',
  'bar',
  'pie',
  'radar',
  'funnel',
  'table',
  'kpi',
];

/** Charts with a category axis and a value axis. */
export const kBiCartesianTypes: readonly BiVizType[] = [
  BiVizType.Line,
  BiVizType.Area,
  BiVizType.Bar,
  BiVizType.HorizontalBar,
  BiVizType.StackedBar,
];

/** The most series a radar draws: measures, or `seriesBy` values. */
export const kBiMaxRadarSeries = 5;

/** The fewest spokes (values of `x`) a radar's polygon reads with. */
export const kBiMinRadarSpokes = 3;

/** A pyramid's sides: two measures, or two `seriesBy` values. */
export const kBiPyramidSides = 2;

/** A funnel's stages: measures, or values of its stage dimension. */
export const kBiMinFunnelStages = 2;
export const kBiMaxFunnelStages = 10;

/** The charts whose series may each name their own type and axis. */
export const kBiComboChartTypes: readonly BiVizType[] = [
  BiVizType.Line,
  BiVizType.Area,
  BiVizType.Bar,
  BiVizType.StackedBar,
];

/** How a chart's series is drawn: its `as`, or the chart's own type. */
export function biSeriesTypeOf(
  viz: BiChartViz,
  series: BiVizSeries,
): BiSeriesType {
  if (series.as) return series.as;
  if (viz.type === BiVizType.Line) return BiSeriesType.Line;
  if (viz.type === BiVizType.Area) return BiSeriesType.Area;
  return BiSeriesType.Bar;
}

/** Some series is drawn on the right axis. */
export function biVizHasRightAxis(viz: BiVizSpec): boolean {
  return (
    isBiChartViz(viz) &&
    viz.series.some(entry => entry.axis === BiAxisSide.Right)
  );
}

const kChartTypes: readonly BiVizType[] = [
  ...kBiCartesianTypes,
  BiVizType.Pie,
  BiVizType.Donut,
  BiVizType.Radar,
  BiVizType.Pyramid,
];

/** The viz types each style option applies to. */
export const kBiVizStyleOptionTypes: Record<
  BiVizStyleOption,
  readonly BiVizType[]
> = {
  title: Object.values(BiVizType),
  subtitle: Object.values(BiVizType),
  palette: [...kChartTypes, BiVizType.Funnel, BiVizType.KpiGroup],
  seriesColors: kChartTypes,
  legend: kChartTypes,
  axes: kBiCartesianTypes,
  gridLines: kBiCartesianTypes,
  dataLabels: [...kChartTypes, BiVizType.Funnel],
  line: [BiVizType.Line, BiVizType.Area],
  bar: [
    BiVizType.Bar,
    BiVizType.HorizontalBar,
    BiVizType.StackedBar,
    BiVizType.Pyramid,
  ],
  pie: [BiVizType.Pie, BiVizType.Donut],
  radar: [BiVizType.Radar],
  funnel: [BiVizType.Funnel],
  table: [...kChartTypes, BiVizType.Funnel, BiVizType.Table],
  kpi: [BiVizType.Kpi, BiVizType.KpiGroup],
};

/**
 * The style options that fit a viz: what the editor offers and the parser
 * accepts. A KPI's delta options need its comparison; on a combo chart
 * `line` fits once a series is drawn as a line or area, and `bar` once
 * one is drawn as bars.
 */
export function biVizStyleOptionsFor(viz: BiVizSpec): BiVizStyleOption[] {
  const drawn =
    isBiChartViz(viz) && kBiComboChartTypes.includes(viz.type)
      ? viz.series.map(entry => biSeriesTypeOf(viz, entry))
      : null;
  return kBiVizStyleOptions.filter(option => {
    if (option === 'kpi') return isBiKpiViz(viz) && viz.compare !== undefined;
    if (drawn && option === 'line')
      return drawn.some(type => type !== BiSeriesType.Bar);
    if (drawn && option === 'bar') return drawn.includes(BiSeriesType.Bar);
    return kBiVizStyleOptionTypes[option].includes(viz.type);
  });
}

/** How the client and the change-set diff name each palette. */
export const kBiPaletteNames: Record<BiPalette, string> = {
  [BiPalette.Default]: '默认',
  [BiPalette.Warm]: '暖色',
  [BiPalette.Cool]: '冷色',
  [BiPalette.Calm]: '柔和',
  [BiPalette.Vivid]: '鲜艳',
  [BiPalette.Mono]: '单色',
  [BiPalette.Business]: '商务',
  [BiPalette.Morandi]: '莫兰迪',
  [BiPalette.Earth]: '大地',
  [BiPalette.Forest]: '森林',
  [BiPalette.Candy]: '糖果',
  [BiPalette.Ocean]: '海洋',
  [BiPalette.Sunset]: '日落',
  [BiPalette.Retro]: '复古',
  [BiPalette.Chinese]: '中国风',
};

export const kBiColorNames: Record<BiColor, string> = {
  [BiColor.Blue]: '蓝色',
  [BiColor.Sky]: '天蓝',
  [BiColor.Cyan]: '青色',
  [BiColor.Teal]: '蓝绿',
  [BiColor.Green]: '绿色',
  [BiColor.Lime]: '黄绿',
  [BiColor.Yellow]: '黄色',
  [BiColor.Amber]: '琥珀色',
  [BiColor.Orange]: '橙色',
  [BiColor.Red]: '红色',
  [BiColor.Rose]: '玫红',
  [BiColor.Pink]: '粉色',
  [BiColor.Purple]: '紫色',
  [BiColor.Violet]: '紫罗兰',
  [BiColor.Indigo]: '靛蓝',
  [BiColor.Navy]: '深蓝',
  [BiColor.Slate]: '石板灰',
  [BiColor.Gray]: '灰色',
  [BiColor.Gold]: '金色',
  [BiColor.Brown]: '棕色',
  [BiColor.Olive]: '橄榄绿',
  [BiColor.Pine]: '墨绿',
  [BiColor.Emerald]: '翠绿',
  [BiColor.Mint]: '薄荷绿',
  [BiColor.Lavender]: '薰衣草紫',
  [BiColor.Coral]: '珊瑚色',
  [BiColor.Sage]: '灰绿',
  [BiColor.Mauve]: '豆沙色',
  [BiColor.Mist]: '雾蓝',
  [BiColor.Sand]: '沙色',
  [BiColor.Taupe]: '灰褐',
  [BiColor.Wine]: '酒红',
};

/** How the editor and the change-set diff name each style option. */
export const kBiVizStyleOptionNames: Record<BiVizStyleOption, string> = {
  title: '标题',
  subtitle: '副标题',
  palette: '配色',
  seriesColors: '系列颜色',
  legend: '图例',
  axes: '坐标轴',
  gridLines: '网格线',
  dataLabels: '数据标签',
  line: '折线',
  bar: '柱形',
  pie: '饼图',
  radar: '雷达图',
  funnel: '漏斗图',
  table: '表格',
  kpi: '指标卡',
};

/** A donut's hole when its style does not say: a pie's is 0. */
export const kBiDonutInnerRadius = 0.6;

/**
 * The look of an option a style leaves out: the client renders these, and
 * a partial option (`line: {width: 3}`) is completed from them.
 */
export const kBiVizStyleDefaults: Required<
  Omit<BiVizStyle, 'subtitle' | 'palette' | 'seriesColors' | 'axes'>
> & {
  axes: { x: BiCategoryAxisStyle; y: BiValueAxisStyle; y2: BiValueAxisStyle };
} = {
  title: { show: true, align: BiTitleAlign.Left },
  legend: { show: true, position: BiLegendPosition.Bottom },
  axes: {
    // No angle: level while the labels fit, else turned 45°.
    x: { show: true },
    y: { show: true },
    y2: { show: true },
  },
  gridLines: true,
  dataLabels: false,
  line: { width: 2, smooth: false, showPoints: true },
  bar: { cornerRadius: 4, gap: BiBarGap.Normal },
  pie: { innerRadius: 0, showPercent: false },
  radar: { fillOpacity: 0.2 },
  funnel: { show: BiFunnelShow.Both },
  table: { density: BiTableDensity.Normal, zebra: false, totalsRow: false },
  kpi: { showDelta: true, deltaGoodWhen: BiDeltaDirection.Up },
};

function isPieType(type: BiVizType): boolean {
  return type === BiVizType.Pie || type === BiVizType.Donut;
}

/**
 * What `seriesColors` keys name on a viz: its measures, the values of its
 * `seriesBy` dimension, or its pie slices (values of `x`).
 */
function colorDomainOf(viz: BiVizSpec): string | null {
  if (!isBiChartViz(viz)) return null;
  if (viz.seriesBy) return `by:${viz.seriesBy}`;
  return isPieType(viz.type) ? `slice:${viz.x}` : 'measures';
}

/**
 * The part of a style that still fits `viz`, for a viz whose type, series
 * or query changed from `previous`: options for another type are dropped
 * (as the editor drops them on save), and so are colors of series no
 * longer charted, or all of them when they named something else.
 */
export function biVizStyleFitting(
  style: BiVizStyle | undefined,
  viz: BiVizSpec,
  previous?: BiVizSpec,
): BiVizStyle | undefined {
  if (!style) return undefined;
  const fits = biVizStyleOptionsFor(viz);
  const kept: BiVizStyle = {};
  for (const option of fits) {
    if (style[option] === undefined) continue;
    (kept as Record<string, unknown>)[option] = style[option];
  }
  const domain = colorDomainOf(viz);
  if (previous && kept.seriesColors && colorDomainOf(previous) !== domain)
    delete kept.seriesColors;
  if (kept.seriesColors && domain === 'measures') {
    const keys = (viz as BiChartViz).series.map(entry => entry.key);
    const colors = Object.entries(kept.seriesColors).filter(([key]) =>
      keys.includes(key),
    );
    if (colors.length) kept.seriesColors = Object.fromEntries(colors);
    else delete kept.seriesColors;
  }
  if (kept.axes?.y2 && !biVizHasRightAxis(viz)) {
    const axes = { ...kept.axes };
    delete axes.y2;
    if (Object.keys(axes).length) kept.axes = axes;
    else delete kept.axes;
  }
  return Object.keys(kept).length ? kept : undefined;
}
